import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";

import type { IQuery } from "@citrine/client";
import ClientBase from "@citrine/client";
import { getLookupMap } from "@citrine/utils/helpers";

import type {
  IGEMTable,
  IGEMTableCSVContent,
  IGEMTableConfigMetadata,
  IGEMTableContentSummary,
  TableIdentifier,
} from "../gem-table";

interface GEMTableQuery extends IQuery {
  page?: number;
  per_page?: number;
  filter?: string;
}

export class GEMTableClient extends ClientBase<
  IGEMTable,
  TableIdentifier,
  GEMTableQuery
> {
  protected constructor(projectId: string) {
    super({
      endpoint: `/api/v1/projects/${projectId}/gem-tables`,
      findField: "entries",
      defaultQuery: {
        page: 1,
        per_page: 100,
        filter: undefined,
      },
    });
  }

  static getInstance(projectId: string) {
    return new this(projectId);
  }

  getId(model: IGEMTable): TableIdentifier {
    return {
      id: model.table.id,
      version: model.table.version,
    };
  }

  getLookupMap(models: IGEMTable[]) {
    return getLookupMap(models, ({ table }) => table.id);
  }

  getUri(identifier?: TableIdentifier): string {
    return `${this.endpoint}/${identifier?.id}/versions/${identifier?.version}`;
  }

  async getTableConfigMetadata(
    identifier: TableIdentifier,
  ): Promise<IGEMTableConfigMetadata> {
    const payload = await this.request<IGEMTableConfigMetadata>({
      url: `${this.getUri(identifier)}/definition`,
    });

    return payload;
  }

  useGetTableConfigMetadata(
    identifier?: TableIdentifier,
    options: Omit<
      UseQueryOptions<IGEMTableConfigMetadata>,
      "queryKey" | "queryFn"
    > = {},
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(identifier), "metadata"],
      queryFn: () => this.getTableConfigMetadata(identifier!),
      enabled: Boolean(identifier && (options.enabled ?? true)),
    });
  }

  /**
   * This function returns the table info (headers and rows) so we can display
   * the table - truncated to first 1000 rows in the backend.
   * It also returns the table config metadata so we can show name/desc.
   */
  async getTableContentSummary(
    identifier: TableIdentifier,
  ): Promise<IGEMTableContentSummary> {
    const payload = await this.request<IGEMTableContentSummary>({
      url: `${this.getUri(identifier)}/content-summary`,
    });

    return payload;
  }

  useGetTableContentSummary(
    identifier?: TableIdentifier,
    options: Omit<
      UseQueryOptions<IGEMTableContentSummary>,
      "queryKey" | "queryFn"
    > = {},
  ): UseQueryResult<IGEMTableContentSummary> {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(identifier), "summary"],
      queryFn: () => this.getTableContentSummary(identifier!),
      enabled: Boolean(identifier && (options.enabled ?? true)),
    });
  }

  // this function actually goes to a different base url for now but hopefully should be merged with gem tables in the future
  // it returns csv content of the same table we see, but not truncated.
  // used for the export table to csv button
  async getTableCSVContent(
    projectId: string,
    tableId: string,
    version: string | number,
    query?: IQuery,
  ): Promise<IGEMTableCSVContent> {
    const payload = await this.request<IGEMTableCSVContent>({
      url: `/api/v1/projects/${projectId}/display-tables/${tableId}/versions/${version}/content`,
      params: query,
    });

    return payload;
  }

  /**
   * Needed because we are overriding the `getConfig` method using the content-summary url.
   */
  getModelCacheKey(id?: TableIdentifier) {
    return [this.getUri(id)];
  }

  /**
   * There's no individual GET endpoint for a GEM table that returns an IGEMTable
   * object, so this hacks around that limitation by leveraging the content-summary
   * endpoint and using it to essentially create an IGEMTable object.
   */
  protected getConfig(id?: TableIdentifier): AxiosRequestConfig {
    return {
      ...super.getConfig(id),
      url: `${this.getUri(id)}/content-summary`,
      transformResponse: (stringifiedContentSummary: string) => {
        const contentSummary = JSON.parse(
          stringifiedContentSummary,
        ) as IGEMTableContentSummary;
        return {
          table: contentSummary.table,
          table_config: contentSummary.table_config,
          table_content_truncated: contentSummary.table_content_truncated,
        };
      },
    };
  }

  protected findConfig(query?: GEMTableQuery): AxiosRequestConfig {
    return {
      ...super.findConfig(query),
      url: `${this.endpoint}/latest`,
    };
  }
}
